// --- React
import React, { useState, useMemo } from 'react';
// --- Date Library
import moment from 'moment';
// --- External UI Libraries
import Tabs from '@dtci/spellbook/Tabs';
import Tab from '@dtci/spellbook/Tab';
import Card from '@dtci/spellbook/Card';
import Typography from '@dtci/spellbook/Typography';
import Input from '@dtci/spellbook/Input';
// --- Components
import { SummaryCard } from '../details-cards/summary';
import { MetadataCard } from '../details-cards/metadata';
import { ProspectCard } from '../details-cards/prospect';
import { SpartaCard } from '../details-cards/sparta';
import { NielsenCard } from '../details-cards/nielsen';
import { LoadCard } from '../details-cards/load';
import { CopyButton, StatusBadge } from '../components';

/**
 * Tab configuration with improved labels and icons
 */
const TAB_CONFIG = [
    { key: 'summary', title: 'Overview', icon: 'fa-info-circle' },
    { key: 'metadata', title: 'Metadata', icon: 'fa-tags' },
    { key: 'prospect', title: 'Prospect', icon: 'fa-chart-line' },
    { key: 'sparta', title: 'Box Office', icon: 'fa-money-bill' },
    { key: 'nielsen', title: 'Ratings', icon: 'fa-star' },
    { key: 'load', title: 'Load Status', icon: 'fa-upload' },
];

/**
 * Tabs Content for Product Details
 *
 * Features:
 * - Sticky header with key product info and status badge
 * - Search/filter functionality to find fields quickly
 * - Improved tab labels with icons
 * - Displays tabs for different sections of product details
 *
 * @param details
 * @returns {Element}
 * @constructor
 */
export const TabsContent = ({ details }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    
    const formattedCreatedDate = useMemo(() => {
        return details.createdAt ? moment(details.createdAt).format('YYYY-MM-DD') : '—';
    }, [details.createdAt]);

    // Tab content components
    const tabContent = useMemo(() => ({
        summary: <SummaryCard details={details} searchTerm={searchTerm} />,
        metadata: <MetadataCard details={details} searchTerm={searchTerm} />,
        prospect: <ProspectCard details={details} searchTerm={searchTerm} />,
        sparta: <SpartaCard details={details} searchTerm={searchTerm} />,
        nielsen: <NielsenCard details={details} searchTerm={searchTerm} />,
        load: <LoadCard details={details} searchTerm={searchTerm} />,
    }), [details, searchTerm]);

    const currentTabKey = TAB_CONFIG[activeTab]?.key || 'summary';

    return (
        <div className="details-drawer-content">
            {/* Sticky Header */}
            <div className="drawer-sticky-header">
                <div className="drawer-sticky-header__top">
                    <div className="drawer-sticky-header__title-row">
                        <Typography variant="T30" bold className="drawer-sticky-header__title">
                            {details.title || 'Untitled Product'}
                        </Typography>
                        <StatusBadge status={details.status} />
                    </div>
                    <div className="drawer-sticky-header__ids">
                        <span className="drawer-sticky-header__id">
                            <span className="drawer-sticky-header__id-label">Product ID:</span>
                            <span className="drawer-sticky-header__id-value">{details.productId || '—'}</span>
                            <CopyButton value={details.productId} />
                        </span>
                        <span className="drawer-sticky-header__id">
                            <span className="drawer-sticky-header__id-label">Group:</span>
                            <span className="drawer-sticky-header__id-value">
                                {details.groupTitle ? `${details.groupTitle} (${details.groupId})` : '—'}
                            </span>
                        </span>
                        {details.episodeNum && (
                            <span className="drawer-sticky-header__id">
                                <span className="drawer-sticky-header__id-label">Episode:</span>
                                <span className="drawer-sticky-header__id-value">
                                    {details.episodeTitle && details.episodeTitle.trim()
                                        ? `${details.episodeNum} - (${details.episodeTitle})`
                                        : `${details.episodeNum} -`
                                    }
                                </span>
                            </span>
                        )}
                        <span className="drawer-sticky-header__id">
                            <span className="drawer-sticky-header__id-label">Created:</span>
                            <span className="drawer-sticky-header__id-value">{formattedCreatedDate}</span>
                        </span>
                    </div>
                </div>

                {/* Search Input */}
                <div className="drawer-search">
                    <Input
                        placeholder="Search fields..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        width="large"
                        className="drawer-search__input"
                    />
                </div>
            </div>

            {/* Tabs and Content */}
            <Card cardStyle="floating" className="drawer-tabs-card">
                <Tabs role="tablist" className="drawer-tabs">
                    {TAB_CONFIG.map((tab, index) => (
                        <Tab
                            key={tab.key}
                            onClick={() => setActiveTab(index)}
                            active={index === activeTab}
                            className="drawer-tab"
                        >
                            <i className={`fa-solid ${tab.icon} drawer-tab__icon`} />
                            {tab.title}
                        </Tab>
                    ))}
                </Tabs>
                <div className="drawer-tab-content">
                    {tabContent[currentTabKey]}
                </div>
            </Card>
        </div>
    );
};
